import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import LeetCodeIntegrationModal from '../../components/LeetCodeIntegrationModal';
import axiosInstance from '../../services/axiosInstance';

const INITIAL_PLATFORMS = [
  {
    id: 'leetcode',
    name: 'LeetCode',
    status: 'Not Connected',
    lastUpdated: '—',
    rawUpdatedAt: null,   // ISO string – used for stale-credential check
    dbStatus: null,       // 'active' | 'expired' | null
    session: '',
    csrfToken: '',
  },
];

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Derive the MUI Chip props for the Status column.
 *
 * Priority:
 *   1. dbStatus === 'expired'          → red   "Expired"
 *   2. rawUpdatedAt older than 7 days  → yellow "May Expire Soon"
 *   3. session present                 → green  "Connected"
 *   4. fallback                        → grey   "Not Connected"
 */
const getStatusChip = ({ dbStatus, rawUpdatedAt, status }) => {
  if (dbStatus === 'expired') {
    return <Chip label="Expired" size="small" color="error" variant="filled" />;
  }

  if (rawUpdatedAt && Date.now() - new Date(rawUpdatedAt).getTime() > SEVEN_DAYS_MS) {
    return (
      <Chip label="May Expire Soon" size="small" color="warning" variant="filled" />
    );
  }

  if (status === 'Connected') {
    return <Chip label="Connected" size="small" color="success" variant="filled" />;
  }

  return <Chip label="Not Connected" size="small" color="default" variant="outlined" />;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const CodingPlatformsPage = () => {
  const [platforms, setPlatforms] = useState(INITIAL_PLATFORMS);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [activePlatform, setActivePlatform] = useState(null);

  // ── Fetch current integration status on mount ──────────────────────────
  useEffect(() => {
    const fetchLeetCodeConfig = async () => {
      setLoading(true);
      setFetchError('');
      try {
        const { data } = await axiosInstance.get('/admin/platforms/leetcode');
        setPlatforms((prev) =>
          prev.map((p) =>
            p.id === 'leetcode'
              ? {
                  ...p,
                  status: data.session ? 'Connected' : 'Not Connected',
                  lastUpdated: formatDate(data.updatedAt),
                  rawUpdatedAt: data.updatedAt ?? null,
                  dbStatus: data.status ?? null,
                  session: data.session ?? '',
                  csrfToken: data.csrfToken ?? '',
                }
              : p
          )
        );
      } catch (err) {
        // 404 means not yet configured – keep default "Not Connected" row
        if (err?.response?.status !== 404) {
          setFetchError('Failed to load platform integration status.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLeetCodeConfig();
  }, []);

  const handleConfigure = (platform) => {
    setActivePlatform(platform);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setActivePlatform(null);
  };

  const handleSaveSuccess = (updated) => {
    setPlatforms((prev) =>
      prev.map((p) =>
        p.id === activePlatform?.id
          ? { ...p, status: updated.status, lastUpdated: updated.lastUpdated }
          : p
      )
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Coding Platforms
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage coding platform integrations used by StackTrack to track student
          progress.
        </Typography>
      </Box>

      {/* Error banner */}
      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fetchError}
        </Alert>
      )}

      {/* Platforms Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Platform</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : (
              platforms.map((platform) => (
                <TableRow key={platform.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {platform.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(platform)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {platform.lastUpdated}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SettingsIcon fontSize="small" />}
                      onClick={() => handleConfigure(platform)}
                    >
                      Configure
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* LeetCode Integration Modal */}
      {activePlatform?.id === 'leetcode' && (
        <LeetCodeIntegrationModal
          open={modalOpen}
          onClose={handleClose}
          initialData={{
            status: activePlatform.status,
            lastUpdated: activePlatform.lastUpdated,
            session: activePlatform.session ?? '',
            csrfToken: activePlatform.csrfToken ?? '',
          }}
          onSaveSuccess={handleSaveSuccess}
        />
      )}
    </Container>
  );
};

export default CodingPlatformsPage;
